import { useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Skeleton from "react-loading-skeleton";
import { Spinner } from "@chakra-ui/react";
import { FiShield } from "react-icons/fi";
import { TabHeader } from "components/TabHeader";
import FormHeader from "components/FormHeading/FormHeader";
import { Button } from "newui/components/Button";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { useCurrentUser } from "hooks/auth";
import { useHasTwoFactorAuth } from "hooks/featureFlags";
import { useHasNoRbacAccess } from "hooks/auth";
import { LimitedAccessNotice } from "newui/features/RolesPermissions";
import { useAppDispatch } from "newui/store/hooks";
import { update_user } from "actions/AccountManagementActions";
import { showToast } from "utils/helpers";
import { USER_ROLE } from "newui/features/Auth/constants/UserRoleConstants";
import { UserAccessMatrixModal } from "newui/features/Settings/components/RolesPermissions/UserAccessMatrixModal";
import { ChangePasswordTrigger } from "../components/Profile/ChangePasswordTrigger";
import { TwoFactorAuthTrigger } from "../components/Profile/TwoFactorAuthTrigger";

type MyProfilePageProps = RouteComponentProps;

interface ProfileFormValues {
	id: number;
	first_name: string;
	last_name: string;
	phone: string;
	email: string;
	/**
	 * PRO-731: the "User Role" dropdown has been REMOVED from the UI — it was
	 * already `disabled` (view-only) and is invalid under RBAC. This value is
	 * still sent, unchanged, purely because the backend's
	 * `UserCreateOrUpdateRequest` marks `role` as `required` and
	 * `UserController@update` uses it to re-sync the user's role assignment
	 * on every save (procuracon-api: app/Http/Requests/Api/V1/User/
	 * UserCreateOrUpdateRequest.php:82, UserController.php update()).
	 * Omitting it entirely would 422 the request. It is derived only from
	 * `currentUser` and is never rendered or user-editable, so there is no
	 * UI-driven privilege-escalation vector here — see PRO-731 report.
	 */
	role: string;
}

const PHONE_REGEX =
	/^(?=.*)((?:\+?61) ?(?:\((?=.*\)))?([2-47-8])\)?|(?:\((?=.*\)))?([0-1][2-47-8])\)?) ?-?(?=.*)((\d{1} ?-?\d{3}$)|(00 ?-?\d{4} ?-?\d{4}$)|( ?-?\d{4} ?-?\d{4}$)|(\d{2} ?-?\d{3} ?-?\d{3}$))/;

const validationSchema = Yup.object({
	first_name: Yup.string().required("This is a required field"),
	last_name: Yup.string().required("This is a required field"),
	email: Yup.string().required("This is a required field"),
	phone: Yup.string()
		.required("This is a required field")
		.matches(PHONE_REGEX, "Invalid Phone No."),
});

/**
 * PRO-731 — My Profile, migrated from the deprecated
 * `components/EditUserProfile/EditUserProfile.js`.
 *
 * Changes from the legacy page:
 *  - the (already-disabled) "User Role" dropdown is gone from the UI
 *  - Change Password and Two-Factor Authentication move to the top of the
 *    page, side by side, and open in modals instead of expanding inline
 *  - a new "View Roles & Permissions" action opens the same read-only
 *    access matrix component used on the User Management screen, in its
 *    `mode="self"` variant (PRO-731 bug A) — the admin default also fetches
 *    `/rbac/modules` and `/rbac/users` (System Admin/admin-only) purely to
 *    label rows and populate a "Project & RFx" tab, and folds their 403s into
 *    the modal's error state even for a non-admin who CAN read their own
 *    `/rbac/users/{id}/permissions`. Self mode skips those two calls entirely.
 */
export const MyProfilePage = (_props: MyProfilePageProps) => {
	const { currentUser } = useCurrentUser() || {};
	const hasTwoFactorAuth = useHasTwoFactorAuth();
	// PRO-737: this page is the ONLY surface a user with no RBAC standing can
	// reach, so it carries the explanation and the contact list.
	const hasNoRbacAccess = useHasNoRbacAccess();
	const dispatch = useAppDispatch();
	const [isAccessMatrixOpen, setIsAccessMatrixOpen] = useState(false);

	if (!currentUser) {
		return (
			<Skeleton
				count={5}
				duration={0.5}
			/>
		);
	}

	const userRole = currentUser.company_admin ? USER_ROLE.ADMINISTRATOR : USER_ROLE.USER;

	const initialValues: ProfileFormValues = {
		id: currentUser.id,
		first_name: currentUser.first_name || "",
		last_name: currentUser.last_name || "",
		phone: currentUser.phone || "",
		email: currentUser.email || "",
		role: userRole.name,
	};

	const handleSubmit = async (values: ProfileFormValues) => {
		// apiV1's response interceptor already surfaces an error toast on
		// failure (see src/api-v2.js), so only the success path needs handling
		// here.
		const user = await dispatch(update_user(values, true));
		if (user) {
			showToast("Updated user Profile", "Success", true);
		}
	};

	return (
		<div data-testid="my-profile-page">
			<TabHeader heading="My Profile" />

			{hasNoRbacAccess && (
				<div className="px-12 pt-6">
					<LimitedAccessNotice />
				</div>
			)}

			<div className="flex flex-wrap items-center gap-4 px-12 pt-6" data-testid="profile-security-actions">
				<ChangePasswordTrigger />
				{hasTwoFactorAuth && (
					<span data-testid="two-factor-trigger-wrapper">
						<TwoFactorAuthTrigger />
					</span>
				)}
			</div>

			<Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ isSubmitting, isValid, submitForm }) => (
					<Form>
						<div className="px-12 pt-8">
							<div className="grid grid-cols-12 gap-6 gap-y-0" data-testid="your-details-section">
								<FormHeader
									className="col-span-12 mb-6"
									header="Your Details"
									subHeader="Update your personal details here."
								/>
								<FormikInputWithLabel
									name="first_name"
									label="First Name"
									placeholder="Enter your first name"
									required
									className="col-span-6"
									testId="first-name-input"
								/>
								<FormikInputWithLabel
									name="last_name"
									label="Last Name"
									placeholder="Enter your last name"
									required
									className="col-span-6"
									testId="last-name-input"
								/>
								<FormikInputWithLabel
									name="phone"
									label="Phone Number"
									placeholder="Enter your phone number"
									required
									className="col-span-6"
									testId="phone-number-input"
								/>
								<FormikInputWithLabel
									name="email"
									label="Email Address"
									placeholder="Enter your email address"
									disabled
									className="col-span-6"
									testId="email-input"
								/>
							</div>

							<div className="mt-8 flex items-center gap-4">
								<Button
									type="button"
									btntype="primary"
									data-testid="save-details-button"
									onClick={submitForm}
									disabled={!isValid || isSubmitting}
								>
									{isSubmitting ? (
										<div className="flex items-center gap-2">
											<Spinner size="sm" /> Saving
										</div>
									) : (
										"Save Details"
									)}
								</Button>
							</div>

							{/*
							  * PRO-737 outcome 4: a user with no roles has no access
							  * matrix worth opening — the modal would render an empty
							  * grid and read as a fault rather than as the explained
							  * state the notice above already gives them.
							  */}
							{!hasNoRbacAccess && (
								<div className="mt-8">
									<Button
										type="button"
										btntype="secondary"
										className="flex items-center gap-2"
										onClick={() => setIsAccessMatrixOpen(true)}
										data-testid="view-roles-permissions-button"
									>
										<FiShield />
										View Roles &amp; Permissions
									</Button>
								</div>
							)}
						</div>
					</Form>
				)}
			</Formik>

			<UserAccessMatrixModal
				isOpen={isAccessMatrixOpen}
				userId={currentUser.id}
				userName={currentUser.full_name || `${currentUser.first_name} ${currentUser.last_name}`.trim()}
				onClose={() => setIsAccessMatrixOpen(false)}
				// PRO-731 bug A — this is the signed-in user viewing their OWN access,
				// so it must use the self-scoped mode rather than the admin default,
				// which also depends on two System Admin/admin-only endpoints this
				// user cannot read. See the `mode` prop doc-comment on
				// UserAccessMatrixModal for the corrected root cause.
				mode="self"
			/>
		</div>
	);
};
